import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { cardColors } from "@plasma/data/card-colors";
import { randomIndex } from "@plasma/utils/time";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { twJoin } from "tailwind-merge";

import {
  categoriesGetAll,
  itemsGetAll,
  printerGetAllByLocationId,
  stationsCreate,
} from "~/api-refs";
import {
  CARD_COLOR_KEYS,
  getCardColorStyle,
} from "~/components/stations/station-edit-types";
import {
  StepPrinting,
  StepReview,
} from "~/components/stations/station-wizard-printing";
import {
  StepBasicInfo,
  StepIndicator,
  StepRouting,
} from "~/components/stations/station-wizard-steps";
import type { StationColorType } from "~/components/stations/station-wizard-types";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/locations/$id/stations/new"
)({
  component: NewStationModal,
});

function NewStationModal() {
  const { id: locationId } = Route.useParams();
  const navigate = useNavigate();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  // Station data
  const [name, setName] = useState("");
  const [hasScreen, setHasScreen] = useState(true);
  const [routingMode, setRoutingMode] = useState<
    "all" | "categories" | "items"
  >("all");
  const [categoriesSelected, setCategoriesSelected] = useState<string[]>([]);
  const [itemsSelected, setItemsSelected] = useState<string[]>([]);
  const [selectedPrinterIds, setSelectedPrinterIds] = useState<string[]>([]);
  const [autoPrint, setAutoPrint] = useState<"off" | "onReceived" | "onBump">(
    "off"
  );
  const [printType, setPrintType] = useState<"ticket" | "label">("ticket");
  const [printOrderTypes, setPrintOrderTypes] = useState<string[]>([
    "dineIn",
    "takeout",
    "delivery",
  ]);
  const [searchValue, setSearchValue] = useState("");

  // Color
  const [colorName, setColorName] = useState<string>("blue");
  const [color, setColor] = useState<StationColorType>(cardColors.blue);

  useEffect(() => {
    const randomColor =
      CARD_COLOR_KEYS[randomIndex(CARD_COLOR_KEYS.length)] ?? "blue";
    setColorName(randomColor);
    setColor(getCardColorStyle(randomColor));
  }, []);

  // Fetch printers
  const { data: printers } = useQuery(
    convexQuery(printerGetAllByLocationId, {
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const printersAvailable =
    printers?.map((p) => ({ value: p._id, label: p.name })) ?? [];

  // Fetch categories and items
  const { data: categoriesResult } = useQuery({
    ...convexQuery(categoriesGetAll, {
      includeItemsCount: true,
      paginationOpts: { numItems: 1000, cursor: null },
    }),
    enabled: routingMode === "categories",
  });

  const { data: itemsResult } = useQuery({
    ...convexQuery(itemsGetAll, {
      paginationOpts: { numItems: 1000, cursor: null },
    }),
    enabled: routingMode === "items",
  });

  const categories = categoriesResult?.page ?? [];
  const items = itemsResult?.page ?? [];

  // Create station mutation
  const createStationMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      name: string;
      color: string;
      hasScreen: boolean;
      routingMode: "all" | "categories" | "items";
      categoryIds?: Id<"categories">[];
      itemIds?: Id<"items">[];
      printerIds?: Id<"printers">[];
      printSettings?: {
        autoPrint: "off" | "onReceived" | "onBump";
        printType: "ticket" | "label";
        printOrderTypes: string[];
      };
    }) => convexClient.mutation(stationsCreate, data),
    onSuccess: (result) => {
      if (result?.id) {
        ProductEvents.stationCreated(String(result.id), locationId);
      }
    },
  });

  const toggleSelection = (id: string, type: "category" | "item") => {
    if (type === "category") {
      setCategoriesSelected((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    } else {
      setItemsSelected((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    }
  };

  const handleClose = () => {
    void navigate({
      to: "/locations/$id/stations",
      params: { id: locationId },
    });
  };

  // Calculate total steps based on routing mode
  const getTotalSteps = () => (routingMode === "all" ? 3 : 4);

  // Get display step (accounting for skipped routing step when routingMode is 'all')
  const getDisplayStep = () => {
    if (routingMode === "all") {
      if (currentStep === 0) {
        return 0;
      }
      if (currentStep === 2) {
        return 1;
      }
      if (currentStep === 3) {
        return 2;
      }
    }
    return currentStep;
  };

  const validateBasicInfoStep = (): boolean => {
    if (!name.trim()) {
      setErrorMessage("Please enter a station name");
      return false;
    }
    return true;
  };

  const validateRoutingStep = (): boolean => {
    if (routingMode === "categories" && categoriesSelected.length === 0) {
      setErrorMessage("Please select at least one category");
      return false;
    }
    if (routingMode === "items" && itemsSelected.length === 0) {
      setErrorMessage("Please select at least one item");
      return false;
    }
    return true;
  };

  const buildStationPayload = () => ({
    locationId: toConvexTableId("locations", locationId),
    name: name.trim(),
    color: colorName,
    hasScreen,
    routingMode,
    categoryIds:
      routingMode === "categories"
        ? categoriesSelected.map((id) => toConvexTableId("categories", id))
        : undefined,
    itemIds:
      routingMode === "items"
        ? itemsSelected.map((id) => toConvexTableId("items", id))
        : undefined,
    printerIds:
      selectedPrinterIds.length > 0
        ? selectedPrinterIds.map((id) => toConvexTableId("printers", id))
        : undefined,
    printSettings:
      selectedPrinterIds.length > 0
        ? { autoPrint, printType, printOrderTypes }
        : undefined,
  });

  const handleSaveStation = async () => {
    if (createStationMutation.isPending) {
      return;
    }

    try {
      await createStationMutation.mutateAsync(buildStationPayload());
      handleClose();
    } catch (error) {
      logger.exception("Error creating station", error);
      setErrorMessage("Failed to create station. Please try again.");
    }
  };

  const handleNext = async () => {
    setErrorMessage("");

    if (currentStep === 0) {
      if (!validateBasicInfoStep()) {
        return;
      }
      // Skip routing step if routingMode is 'all'
      setCurrentStep(routingMode === "all" ? 2 : 1);
      return;
    }

    if (currentStep === 1) {
      if (!validateRoutingStep()) {
        return;
      }
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      setCurrentStep(3);
      return;
    }

    if (currentStep === 3) {
      await handleSaveStation();
    }
  };

  const handleBack = () => {
    if (currentStep === 2 && routingMode === "all") {
      setCurrentStep(0); // Skip back to basic info
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="sm:max-w-lg" noScrollPrevention>
        <DialogTitle className="sr-only">New Station</DialogTitle>
        <DialogDescription className="sr-only">
          Create a new station
        </DialogDescription>

        <StepIndicator
          currentStep={getDisplayStep()}
          totalSteps={getTotalSteps()}
        />

        <div className="flex flex-col gap-5">
          {currentStep === 0 && (
            <StepBasicInfo
              color={color}
              errorMessage={errorMessage}
              hasScreen={hasScreen}
              name={name}
              routingMode={routingMode}
              setHasScreen={setHasScreen}
              setName={setName}
              setRoutingMode={setRoutingMode}
            />
          )}

          {currentStep === 1 && routingMode !== "all" && (
            <StepRouting
              categories={categories}
              categoriesSelected={categoriesSelected}
              color={color}
              items={items}
              itemsSelected={itemsSelected}
              name={name}
              routingMode={routingMode}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              toggleSelection={toggleSelection}
            />
          )}

          {currentStep === 2 && (
            <StepPrinting
              autoPrint={autoPrint}
              color={color}
              name={name}
              printersAvailable={printersAvailable}
              printOrderTypes={printOrderTypes}
              printType={printType}
              selectedPrinterIds={selectedPrinterIds}
              setAutoPrint={setAutoPrint}
              setPrintOrderTypes={setPrintOrderTypes}
              setPrintType={setPrintType}
              setSelectedPrinterIds={setSelectedPrinterIds}
            />
          )}

          {currentStep === 3 && (
            <StepReview
              autoPrint={autoPrint}
              categoriesSelected={categoriesSelected}
              color={color}
              hasScreen={hasScreen}
              itemsSelected={itemsSelected}
              name={name}
              printersAvailable={printersAvailable}
              routingMode={routingMode}
              selectedPrinterIds={selectedPrinterIds}
            />
          )}

          {errorMessage && currentStep !== 0 && (
            <p className="text-destructive text-center">{errorMessage}</p>
          )}

          <div
            className={twJoin(
              "flex",
              currentStep !== 0 ? "justify-between" : "justify-end"
            )}
          >
            {currentStep !== 0 && (
              <Button onClick={handleBack} variant="secondary">
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
            )}
            <Button
              disabled={createStationMutation.isPending}
              onClick={handleNext}
            >
              {currentStep === 3 ? (
                <>
                  <CheckCircle2 className="mr-1 h-4 w-4" />
                  Create Station
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
